import { Fragment, type ReactNode } from 'react';
import { AlertTriangle, Hourglass, Info } from 'lucide-react';
import { classeBadgeReponse, iconeAlerteBadge, libelleBadgeReponse, type BandeReponse, type Segment, type VueReponse } from './confirmation';

/**
 * Le badge de la réponse du/de la remplaçant·e (docs/DECISIONS.md §38) : bordure tiretée + sablier + mot tant que c'est dans les temps,
 * trait plein + triangle d'alerte en retard, rouge si la séance est dans le seuil « Urgent ». Jamais la couleur seule.
 * `pourLaPersonneDesignee` : c'est elle qui regarde (« À vous de confirmer »).
 */
export function BadgeReponse({ vue, pourLaPersonneDesignee = false, classeIcone = 'cal-icone' }: { vue: VueReponse; pourLaPersonneDesignee?: boolean; classeIcone?: string }) {
  if (vue === 'aucune') return null;
  const Icone = iconeAlerteBadge(vue) ? AlertTriangle : Hourglass;
  return (
    <span className={classeBadgeReponse(vue)}>
      <Icone className={classeIcone} aria-hidden="true" />{libelleBadgeReponse(vue, pourLaPersonneDesignee)}
    </span>
  );
}

/** Un texte à mots en gras (les `Segment` de confirmation.ts). */
export function TexteSegments({ segments }: { segments: Segment[] }) {
  return <>{segments.map((s, i) => (typeof s === 'string' ? <Fragment key={i}>{s}</Fragment> : <b key={i}>{s.b}</b>))}</>;
}

/** La note sous la frise : où en est la réponse, en une phrase (tiretée en violet, pleine et rouge si la séance est dans le seuil « Urgent »). */
export function NoteReponse({ note }: { note: { alerte: boolean; urgent: boolean; segments: Segment[] } | null }) {
  if (!note) return null;
  const Icone = note.alerte ? AlertTriangle : Hourglass;
  return (
    <div className={`frise-note${note.urgent ? ' urgent' : ''}`}>
      <Icone aria-hidden="true" /><span><TexteSegments segments={note.segments} /></span>
    </div>
  );
}

/** La bande « sous réserve » sous les actions de la carte « Prochaine étape » : ce qu'on attend, et les gestes possibles (`children`). */
export function BandeReserve({ bande, children }: { bande: BandeReponse | null; children?: ReactNode }) {
  if (!bande) return null;
  const Icone = bande.alerte ? AlertTriangle : Hourglass;
  return (
    <div className={`pe-reserve${bande.urgent ? ' urgent' : ''}`}>
      <div className="pe-reserve-l"><Icone aria-hidden="true" /><span><TexteSegments segments={bande.segments} /></span></div>
      {children}
    </div>
  );
}

/** Une information neutre sous la carte (par ex. « n'a pas de compte TacTac »). */
export function InfoCarte({ children }: { children: ReactNode }) {
  return <div className="pe-info"><Info aria-hidden="true" /><span>{children}</span></div>;
}
